import { action, computed, makeObservable, observable } from 'mobx';
import { BetStatus } from '@pascal/pascal-core';
import { applicationStore } from '@pascal/pascal-core/shared/core/store';
import { gameStore } from './gameStore';
import { doBetStore } from './doBetStore';
import { CARDS_FRONT_TYPES, UNKNOWN_CARD_TYPE } from '../enums/cardTypes';
import { gameLiveConnection } from '../services';
import { soundService, soundTypes } from '../services/SoundService';

class CardsStore {
  tableCards = [];

  pickCardIndex = null;

  isPickHoldActive = false;

  isShowGambleWin = false;

  selectedCardType = CARDS_FRONT_TYPES[0];

  constructor() {
    makeObservable(this, {
      tableCards: observable.deep,
      pickCardIndex: observable.deep,
      isPickHoldActive: observable.deep,
      isShowGambleWin: observable,
      selectedCardType: observable,
      setTableCards: action.bound,
      setIsPickHoldActive: action.bound,
      setPickCardIndex: action.bound,
      setWinInCards: action.bound,
      setIsShowGambleWin: action.bound,
      setWithOutWinCards: action.bound,
      closeCards: action.bound,
      setSelectedCardType: action.bound,
      handleChangeHoldInCard: action.bound,
      removeWinInCards: action.bound,
      updateCards: action.bound,
      heldCards: computed,
    });
  }

  setSelectedCardType(type) {
    this.selectedCardType = type;
  }

  setIsShowGambleWin(bool) {
    this.isShowGambleWin = bool;
  }

  setWinInCards(cards, isWithHold) {
    if (isWithHold) {
      this.tableCards = cards.map(({ index, isWin, ...all }) => ({
        isDisabled: doBetStore.isShowGamble && index === 1 ? false : !isWin,
        isWin,
        ...all,
      }));
    } else {
      this.tableCards = cards.map(({ index, isWin, isHold, ...all }) => ({
        isDisabled: !isWin,
        isWin,
        ...all,
      }));
    }
  }

  get heldCards() {
    if (this.tableCards.length) {
      return this.tableCards.filter((item) => item.isHold).map((item) => item.card);
    }
    return [];
  }

  removeWinInCards(isWithoutHold) {
    if (isWithoutHold) {
      this.tableCards = this.tableCards.map(({ isWin, isDisabled, isHold, ...all }) => all);
    } else {
      this.tableCards = this.tableCards.map(({ isWin, isDisabled, ...all }) => all);
    }
  }

  setTableCards(payload, isWithoutWin) {
    if (isWithoutWin) {
      this.tableCards = payload.map(({ isWin, ...all }) => ({ ...all }));
    } else {
      this.tableCards = payload;
    }
  }

  setIsPickHoldActive(bool) {
    this.isPickHoldActive = bool;
  }

  setPickCardIndex(cardIndex) {
    if (this.isPickHoldActive && cardIndex !== 1 && gameStore.isAllowSendToInvoke) {
      this.pickCardIndex = cardIndex;
      if (applicationStore.isDemo) {
        gameLiveConnection.getDemoGambleResult(cardIndex);
      } else {
        gameLiveConnection.getGambleResult({ cardIndex, betId: gameStore.betId });
      }
      gameStore.setIsAllowSendToInvoke(false);
    }
  }

  updateCards(cards, isWithoutHold, onHold) {
    for (let i = 0; i < cards.length; i += 1) {
      const { isWin, isHold, ...all } = cards[i];
      setTimeout(() => {
        if (!onHold) {
          soundService.play(soundTypes.double);
        }
        if (onHold && !this.tableCards.some((item) => item.card === cards[i].card)) {
          soundService.play(soundTypes.double);
        }
        if (this.tableCards.length < 5) {
          const item = isWithoutHold ? { ...all } : { isHold, ...all };
          this.tableCards.push(item);
        } else {
          const item = isWithoutHold ? { ...all } : { isHold, ...all };
          this.tableCards.splice(i, 1, item);
        }
      }, 70 * i);
    }
  }

  setWithOutWinCards(payload, isWithoutHold) {
    if (isWithoutHold) {
      this.tableCards = payload.map(({ isWin, isHold, card, ...all }) => {
        if (doBetStore.isShowGamble) {
          return {
            close: card === UNKNOWN_CARD_TYPE,
            card,
            ...all,
          };
        }
        return {
          card,
          ...all,
        };
      });
    } else {
      this.tableCards = payload.map(({ isWin, card, ...all }) => {
        if (doBetStore.isShowGamble) {
          return {
            close: card === UNKNOWN_CARD_TYPE,
            card,
            ...all,
          };
        }
        return {
          card,
          ...all,
        };
      });
    }
  }

  handleChangeHoldInCard(curIndex) {
    if (gameStore.roundStatus === BetStatus.Pending && !doBetStore.isShowGamble) {
      soundService.play(soundTypes.cardHold);
      const isHoldMaxValid = this.tableCards.reduce((acc, cur) => {
        if (cur.isHold) {
          acc += 1;
        }
        return acc;
      }, 0);

      if ((this.tableCards[curIndex] && isHoldMaxValid < 5) || this.tableCards[curIndex].isHold) {
        this.tableCards = this.tableCards.map((item, index) => {
          if (index === curIndex) {
            return { ...item, isHold: !this.tableCards[curIndex].isHold };
          }
          return item;
        });
      }
    }
  }

  closeCards() {
    this.tableCards = this.tableCards.map((item) => ({ ...item, isShowHeld: false }));
    for (let i = 0; i < this.tableCards.length; i += 1) {
      setTimeout(() => {
        if (!this.heldCards.includes(this.tableCards[i].card)) {
          this.tableCards.splice(i, 1, { ...this.tableCards[i], close: true });
        }
      }, 30 * i);
    }
  }
}

export const cardsStore = new CardsStore();
